/**
 * Schedule Input Block Renderer
 *
 * The `ui_schedule_input` card: "Runs [frequency ▾] [time]" plus, for the
 * Custom cadence, "Repeat every [N] [unit ▾]" and a day-of-week toggle row,
 * with a Confirm button. Cron derivation is deterministic and lives in
 * `utils/scheduleCron` — the card returns `{ frequency, time, cron, label }`
 * and the agent forwards `cron` verbatim to the routine tools. After submit
 * (or rehydration via `submittedSchedule`) the controls render disabled.
 */

import React from "react";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@strivelabs/ui-core";
import type {
  UIBlock,
  UIActionEvent,
  ScheduleFrequency,
  ScheduleDay,
} from "@/types/ui-blocks";
import {
  buildCronFromSchedule,
  describeSchedule,
  WEEK_DAYS,
} from "@/utils/scheduleCron";

const FREQUENCY_LABELS: { value: ScheduleFrequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekdays", label: "Weekdays" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
];

const CUSTOM_UNITS = ["day", "week", "month"] as const;

const DEFAULT_TITLE = "How should I schedule this routine?";
const DEFAULT_DESCRIPTION = "Choose when this routine should run, or describe it in your own words.";

export interface ScheduleInputBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled (historical render). */
  readOnly?: boolean;
}

export function ScheduleInputBlockRenderer({
  block,
  onUIAction,
  readOnly = false,
}: ScheduleInputBlockRendererProps) {
  const content = block.type === "schedule_input" ? block.content : null;
  const submitted = content?.submittedSchedule;

  const [frequency, setFrequency] = React.useState<ScheduleFrequency>(
    submitted?.frequency ?? content?.frequency ?? "daily"
  );
  const [time, setTime] = React.useState<string>(submitted?.time ?? content?.time ?? "09:00");
  const [interval, setIntervalValue] = React.useState<number>(
    Math.max(1, Math.floor(content?.custom?.interval ?? 1))
  );
  const [unit, setUnit] = React.useState<"day" | "week" | "month">(content?.custom?.unit ?? "week");
  const [days, setDays] = React.useState<ScheduleDay[]>(content?.custom?.days_of_week ?? []);
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(Boolean(submitted));

  React.useEffect(() => {
    setFrequency(submitted?.frequency ?? content?.frequency ?? "daily");
    setTime(submitted?.time ?? content?.time ?? "09:00");
    setIntervalValue(Math.max(1, Math.floor(content?.custom?.interval ?? 1)));
    setUnit(content?.custom?.unit ?? "week");
    setDays(content?.custom?.days_of_week ?? []);
    setIsSubmitted(Boolean(submitted));
  }, [content?.title, content?.frequency, content?.time, content?.custom, submitted]);

  if (block.type !== "schedule_input" || !content) return null;

  const disabled = readOnly || isSubmitted;

  const toggleDay = (day: ScheduleDay) => {
    if (disabled) return;
    setDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const handleConfirm = () => {
    if (disabled) return;
    const selection = {
      frequency,
      time,
      custom: frequency === "custom" ? { interval, unit, days_of_week: days } : null,
    };
    const result = {
      frequency,
      time,
      cron: buildCronFromSchedule(selection),
      label: describeSchedule(selection),
    };
    setIsSubmitted(true);
    onUIAction({
      type: "ui_action",
      component: "schedule_input",
      label: content.title ?? DEFAULT_TITLE,
      state: result,
    });
  };

  return (
    <div className="my-2 w-full rounded-lg border border-custom-border bg-[#fbfbfb] p-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-medium text-gray-900">{content.title ?? DEFAULT_TITLE}</h3>
        <p className="text-sm text-gray-500">{content.description ?? DEFAULT_DESCRIPTION}</p>
      </div>

      <div className="flex flex-col gap-3 py-4">
        <div className="flex items-center gap-4">
          <span className="w-24 shrink-0 text-sm font-medium text-gray-500">Runs</span>
          <Select
            value={frequency}
            onValueChange={(next) => setFrequency(next as ScheduleFrequency)}
            disabled={disabled}
          >
            <SelectTrigger className="h-9 w-36 bg-white text-sm">
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              {FREQUENCY_LABELS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="time"
            value={time}
            onChange={(event) => setTime(event.target.value || "09:00")}
            disabled={disabled}
            className="h-9 w-32 bg-white text-sm"
          />
        </div>

        {frequency === "custom" ? (
          <>
            <div className="flex items-center gap-4">
              <span className="w-24 shrink-0 text-sm font-medium text-gray-500">Repeat every</span>
              <Input
                type="number"
                min={1}
                value={interval}
                onChange={(event) => setIntervalValue(Math.max(1, Number(event.target.value) || 1))}
                // Cron can't express "every N weeks" — the interval only
                // applies to day/month units.
                disabled={disabled || unit === "week"}
                className="h-9 w-16 bg-white text-sm"
              />
              <Select
                value={unit}
                onValueChange={(next) => setUnit(next as "day" | "week" | "month")}
                disabled={disabled}
              >
                <SelectTrigger className="h-9 w-28 bg-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CUSTOM_UNITS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {unit === "week" ? (
              <div className="flex items-center gap-4">
                <span className="w-24 shrink-0 text-sm font-medium text-gray-500">Repeat on</span>
                <div className="flex items-center gap-1.5">
                  {WEEK_DAYS.map((day) => {
                    const selected = days.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(day)}
                        disabled={disabled}
                        aria-pressed={selected}
                        className={cn(
                          "flex h-7 w-7 items-center justify-center rounded-md border text-xs font-medium transition-colors",
                          selected
                            ? "border-gray-900 bg-gray-900 text-white"
                            : "border-custom-border bg-white text-gray-500 hover:bg-gray-50",
                          disabled && "cursor-default opacity-70"
                        )}
                      >
                        {day.charAt(0).toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {!disabled ? (
        <div className="flex justify-end">
          <Button variant="black" size="sm" onClick={handleConfirm}>
            Confirm
          </Button>
        </div>
      ) : null}
    </div>
  );
}
